export type ZMailMessage = {
  id: string | number;
  conversation_id?: string | number | null;
  sender_id?: number | null;
  sender_name: string;
  sender_initials: string;
  body: string;
  created_at: string | null;
  viewed?: string | null;
  can_delete?: boolean;
  can_delete_for_me?: boolean;
  can_delete_for_everyone?: boolean;
};

export type ZMailConversation = {
  id: string | number;
  participant_id: number | null;
  participant_name: string;
  participant_initials: string;
  unread_count: number;
  last_message_at: string | null;
  has_more_messages?: boolean;
  next_messages_cursor?: string | null;
  messages: ZMailMessage[];
};

const toNumberOrNull = (value: any): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const textOrEmpty = (value: any): string => (typeof value === 'string' ? value : value == null ? '' : String(value));

const initialsFromName = (name: string): string => {
  const cleaned = textOrEmpty(name).trim();
  if (!cleaned) return '??';
  const words = cleaned.split(/\s+/).filter(Boolean);
  const initials = words.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
  return initials || cleaned.slice(0, 2).toUpperCase();
};

const getMessageBody = (raw: any): string =>
  textOrEmpty(raw?.body ?? raw?.message ?? raw?.text ?? raw?.content).trim();

const getMessageCreatedAt = (raw: any): string | null =>
  raw?.created_at ?? raw?.dateCreated ?? raw?.createdAt ?? raw?.sent_at ?? null;

const getSenderId = (raw: any): number | null =>
  toNumberOrNull(raw?.sender_id ?? raw?.from_user_id ?? raw?.from_id ?? raw?.user_id ?? raw?.created_by);

const getSenderName = (raw: any): string => {
  const candidate = raw?.sender_name ?? raw?.from_name ?? raw?.user_name ?? raw?.from ?? raw?.author_name;
  return textOrEmpty(candidate).trim() || 'Unknown';
};

const getSenderInitials = (raw: any, senderName: string): string => {
  const candidate = raw?.sender_initials ?? raw?.from_initials ?? raw?.user_initials;
  const text = textOrEmpty(candidate).trim();
  return text || initialsFromName(senderName);
};

const normalizeMessage = (raw: any): ZMailMessage | null => {
  const body = getMessageBody(raw);
  if (!body) return null;

  const senderName = getSenderName(raw);

  return {
    id: raw?.id ?? raw?.message_id ?? `${senderName}-${body}-${getMessageCreatedAt(raw) ?? 'n/a'}`,
    conversation_id: raw?.conversation_id ?? raw?.thread_id ?? null,
    sender_id: getSenderId(raw),
    sender_name: senderName,
    sender_initials: getSenderInitials(raw, senderName),
    body,
    created_at: getMessageCreatedAt(raw),
    viewed: raw?.viewed ?? null,
    can_delete: raw?.can_delete,
    can_delete_for_me: raw?.can_delete_for_me,
    can_delete_for_everyone: raw?.can_delete_for_everyone,
  };
};

const normalizeParticipant = (rawConversation: any, firstMessage: ZMailMessage | null, userId: number | null) => {
  const participantId = toNumberOrNull(
    rawConversation?.participant_id ?? rawConversation?.assigned_to ?? rawConversation?.user_id ?? rawConversation?.member_id,
  );

  const participantNameCandidate =
    rawConversation?.participant_name ??
    rawConversation?.to_name ??
    rawConversation?.member_name ??
    rawConversation?.user_name;

  let participantName = textOrEmpty(participantNameCandidate).trim();

  if (!participantName && firstMessage) {
    // Fallback: if first message sender is not current user, use sender as participant.
    participantName = firstMessage.sender_id !== userId ? firstMessage.sender_name : 'Participant';
  }

  const participantInitialsCandidate =
    rawConversation?.participant_initials ?? rawConversation?.to_initials ?? rawConversation?.member_initials;

  const participantInitialsText = textOrEmpty(participantInitialsCandidate).trim();

  return {
    participant_id: participantId,
    participant_name: participantName || 'Participant',
    participant_initials: participantInitialsText || initialsFromName(participantName || 'Participant'),
  };
};

const normalizeConversationRecord = (rawConversation: any, userId: number | null): ZMailConversation | null => {
  const rawMessages =
    (Array.isArray(rawConversation?.messages) && rawConversation.messages) ||
    (Array.isArray(rawConversation?.z_mails) && rawConversation.z_mails) ||
    (Array.isArray(rawConversation?.items) && rawConversation.items) ||
    [];

  const messages = rawMessages
    .map((msg: any) => normalizeMessage(msg))
    .filter(Boolean)
    .sort((a: any, b: any) => {
      const aTs = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bTs = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return aTs - bTs;
    }) as ZMailMessage[];

  const firstMessage = messages.length > 0 ? messages[0] : null;

  if (messages.length === 0 && !rawConversation?.latest_message && !rawConversation?.message) {
    return null;
  }

  if (messages.length === 0 && (rawConversation?.latest_message || rawConversation?.message)) {
    const synthetic = normalizeMessage(rawConversation?.latest_message ?? rawConversation);
    if (synthetic) messages.push(synthetic);
  }

  const participant = normalizeParticipant(rawConversation, firstMessage, userId);

  const unreadCountRaw = rawConversation?.unread_count ?? rawConversation?.unread ?? null;
  let unreadCount = toNumberOrNull(unreadCountRaw) ?? 0;

  if (!unreadCount) {
    unreadCount = messages.filter((msg) => msg.viewed === 'No').length;
  }

  const lastMessageAt =
    rawConversation?.last_message_at ??
    rawConversation?.updated_at ??
    (messages.length > 0 ? messages[messages.length - 1].created_at : null);

  const hasMoreMessagesRaw =
    rawConversation?.has_more_messages ??
    rawConversation?.messages_has_more ??
    rawConversation?.has_more ??
    rawConversation?.meta?.has_more ??
    null;

  const nextMessagesCursorRaw =
    rawConversation?.next_messages_cursor ??
    rawConversation?.messages_next_cursor ??
    rawConversation?.next_cursor ??
    rawConversation?.meta?.next_cursor ??
    null;

  return {
    id: rawConversation?.id ?? rawConversation?.conversation_id ?? rawConversation?.thread_id ?? `c-${participant.participant_id ?? 'unknown'}-${lastMessageAt ?? 'na'}`,
    participant_id: participant.participant_id,
    participant_name: participant.participant_name,
    participant_initials: participant.participant_initials,
    unread_count: unreadCount,
    last_message_at: lastMessageAt,
    has_more_messages: typeof hasMoreMessagesRaw === 'boolean' ? hasMoreMessagesRaw : false,
    next_messages_cursor: nextMessagesCursorRaw == null ? null : String(nextMessagesCursorRaw),
    messages,
  };
};

const normalizeLegacyFlatMessages = (rawMessages: any[], userId: number | null): ZMailConversation[] => {
  const grouped = new Map<string, { key: string; participantId: number | null; participantName: string; participantInitials: string; messages: ZMailMessage[] }>();

  rawMessages.forEach((raw) => {
    const message = normalizeMessage(raw);
    if (!message) return;

    const senderId = message.sender_id;
    const assignedTo = toNumberOrNull(raw?.assigned_to ?? raw?.to_user_id ?? raw?.to_id);

    const participantId = senderId === userId ? assignedTo : senderId;
    const participantName =
      textOrEmpty(raw?.participant_name ?? raw?.to_name ?? raw?.from_name ?? raw?.member_name).trim() ||
      (senderId === userId ? textOrEmpty(raw?.to_name).trim() : message.sender_name) ||
      'Participant';
    const participantInitials =
      textOrEmpty(raw?.participant_initials ?? raw?.to_initials ?? raw?.from_initials).trim() || initialsFromName(participantName);

    const key = String(participantId ?? `legacy-${message.id}`);

    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        participantId,
        participantName,
        participantInitials,
        messages: [],
      });
    }

    grouped.get(key)?.messages.push(message);
  });

  return Array.from(grouped.values())
    .map((item) => {
      const sorted = item.messages.sort((a, b) => {
        const aTs = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const bTs = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return aTs - bTs;
      });
      return {
        id: `legacy-${item.key}`,
        participant_id: item.participantId,
        participant_name: item.participantName,
        participant_initials: item.participantInitials,
        unread_count: sorted.filter((msg) => msg.viewed === 'No').length,
        last_message_at: sorted.length > 0 ? sorted[sorted.length - 1].created_at : null,
        has_more_messages: false,
        next_messages_cursor: null,
        messages: sorted,
      };
    })
    .sort((a, b) => {
      const aTs = a?.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bTs = b?.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bTs - aTs;
    });
};

export const normalizeZMailConversations = (team: any, userId: number | null): ZMailConversation[] => {
  const fromConversationCollection =
    (Array.isArray(team?.z_mail_conversations) && team.z_mail_conversations) ||
    (Array.isArray(team?.z_mail_threads) && team.z_mail_threads) ||
    (Array.isArray(team?.z_mail_chats) && team.z_mail_chats) ||
    null;

  if (fromConversationCollection) {
    return fromConversationCollection
      .map((conversation: any) => normalizeConversationRecord(conversation, userId))
      .filter(Boolean)
      .sort((a: any, b: any) => {
        const aTs = a?.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const bTs = b?.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return bTs - aTs;
      });
  }

  const rawMessages = (Array.isArray(team?.z_mail) && team.z_mail) || [];
  if (rawMessages.length === 0) return [];

  return normalizeLegacyFlatMessages(rawMessages, userId);
};

export const getUnreadConversationCount = (conversations: ZMailConversation[]): number => {
  return conversations.reduce((count, conversation) => count + (conversation.unread_count > 0 ? 1 : 0), 0);
};

export const canDeleteMessage = (message: ZMailMessage, currentUserId: number | null): boolean => {
  if (typeof message.can_delete === 'boolean') return message.can_delete;
  if (message.sender_id == null || currentUserId == null) return false;
  return Number(message.sender_id) === Number(currentUserId);
};

export const canDeleteMessageForEveryone = (message: ZMailMessage, currentUserId: number | null): boolean => {
  if (typeof message.can_delete_for_everyone === 'boolean') return message.can_delete_for_everyone;
  return canDeleteMessage(message, currentUserId);
};

export const canDeleteMessageForMe = (message: ZMailMessage, currentUserId: number | null): boolean => {
  if (typeof message.can_delete_for_me === 'boolean') return message.can_delete_for_me;
  return currentUserId != null;
};
